/* eslint-disable react/button-has-type */ // https://github.com/jsx-eslint/eslint-plugin-react/issues/1555
import { type Theme, useTheme } from '../../../../theme';
import {
  type MouseEvent,
  cloneElement,
  type ReactElement,
  useId,
  type JSX,
} from 'react';
import { getBaseVariant } from './variants/base';
import { getGhostButton } from './variants/ghost';
import { getPrimaryButton } from './variants/primary';
import { getSecondaryButton } from './variants/secondary';
import { getOutlineButton } from './variants/outline';
import { getDestructiveButton } from './variants/destructive';
import { getLinkButton } from './variants/link';
import { getLinkInlineButton } from './variants/linkInline';
import { getSecondaryAltButton } from './variants/secondaryAlt';
import { getUpgradeButton } from './variants/upgrade';
import { getStatusButton } from './variants/status';
import { getOutlineDestructiveButton } from './variants/outlineDestructive';
import { BodyS } from '../../../../typography';
import { Status } from '../../status';
import { useTouchClick } from '../../../../hooks';
import {
  ButtonStyles,
  ButtonType,
  ButtonSize,
  ButtonProps,
} from './button.types';
import { IconProps } from '@doxyme/icons';
import {
  STROKE_WIDTH,
  VIEWBOX_SIZE,
  CENTER,
  RADIUS,
  rotate,
  dash,
} from '../../spinner/spinnerPrimitives';
import { useIntl } from '@doxyme/locale';

const variantOfType = (type: ButtonType): ((theme: Theme) => ButtonStyles) => {
  const variants: Record<ButtonType, (theme: Theme) => ButtonStyles> = {
    primary: getPrimaryButton,
    secondary: getSecondaryButton,
    ghost: getGhostButton,
    outline: getOutlineButton,
    destructive: getDestructiveButton,
    link: getLinkButton,
    linkInline: getLinkInlineButton,
    secondaryAlt: getSecondaryAltButton,
    upgrade: getUpgradeButton,
    outlineDestructive: getOutlineDestructiveButton,
  };

  return variants[type];
};

export const getStylesBySize = ({
  size,
  type,
  theme,
  isLoading,
}: {
  size: ButtonSize;
  type: ButtonType;
  theme: Theme;
  isLoading: boolean;
}): { padding: string; minHeight: number } =>
  ({
    regular: {
      padding: theme.spacing(0, 3),
      minHeight: 40,
      ...(['primary', 'upgrade', 'destructive'].includes(type) && {
        minWidth: 100,
      }),
    },
    small: {
      padding: theme.spacing(1, 2),
      minHeight: 32,
    },
    xsmall: {
      padding: isLoading
        ? `1px ${theme.spacing(3)}`
        : `1px ${theme.spacing(2)}`,
      minHeight: 24,
    },
  }[size]);

export const getIconMarginBySize = (size: ButtonSize): string =>
  ({
    regular: '8px',
    small: '4px',
    xsmall: '2px',
  }[size]);

export const getIconSize = (size: ButtonSize): number =>
  ({
    regular: 20,
    small: 16,
    xsmall: 16,
  }[size]);

const getBorderRadiusBySize = (
  size: ButtonSize,
  theme: Theme,
): { borderRadius: string } =>
  ({
    regular: {
      borderRadius: theme.shape.radius8,
    },
    small: {
      borderRadius: theme.shape.radius8,
    },
    xsmall: {
      borderRadius: theme.shape.radius4,
    },
  }[size]);

const spinnerColorOfType = (type: ButtonType, theme: Theme): string =>
  ({
    primary: theme.animatedSpinner.brand.spinner,
    secondary: theme.animatedSpinner.contrast.spinner,
    secondaryAlt: theme.animatedSpinner.contrast.spinner,
    ghost: theme.animatedSpinner.contrast.spinner,
    outline: theme.animatedSpinner.contrast.spinner,
    outlineDestructive: theme.animatedSpinner.contrast.spinner,
    link: theme.animatedSpinner.link.spinner,
    linkInline: theme.animatedSpinner.link.spinner,
    destructive: theme.animatedSpinner.white.spinner,
    upgrade: theme.animatedSpinner.white.spinner,
  }[type]);

export const Button = ({
  onClick,
  onMouseDown,
  children,
  type = 'primary',
  htmlType = 'button',
  width,
  size = 'regular',
  isLoading = false,
  isDisabled = false,
  startIcon,
  endIcon,
  className,
  innerRef,
  themeName,
  customIconSize,
  status,
  css,
  id,
  ...rest
}: ButtonProps): JSX.Element => {
  const theme = useTheme(themeName);
  const baseStyles = getBaseVariant(theme);
  const variant = variantOfType(type)(theme);
  const sizeVariants = getStylesBySize({ size, type, theme, isLoading });
  const borderRadiusVariants = getBorderRadiusBySize(size, theme);
  const iconMargin = getIconMarginBySize(size);
  const autoId = useId();
  const buttonId = id || `button-${autoId}`;
  const { formatMessage } = useIntl();

  const handleClick = (event: MouseEvent) => {
    if (isDisabled) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    target.blur();
    void onClick?.(event);
  };

  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchClick({
    onClick: onClick ? handleClick : undefined,
  });

  const handleMouseDown = (event: MouseEvent): void => {
    if (isDisabled) {
      return;
    }

    void onMouseDown?.(event);
  };

  if (typeof status !== 'undefined') {
    return (
      <button
        {...rest}
        id={buttonId}
        type={htmlType}
        css={getStatusButton({ theme, type, size })}
        className={className}
        ref={innerRef}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        <Status statusLevel={status} size={size === 'xsmall' ? 20 : 24} />
        <BodyS fontWeight='medium'>{children}</BodyS>
      </button>
    );
  }

  return (
    <button
      {...rest}
      id={buttonId}
      disabled={isDisabled || isLoading}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      type={htmlType}
      className={className}
      ref={innerRef}
      css={{
        ...baseStyles,
        ...borderRadiusVariants,
        ...sizeVariants,
        ...variant.base,
        gap: iconMargin,
        pointerEvents: isLoading ? 'none' : 'auto',
        width,
        ...(isDisabled && variant.disabled),
        ...(isLoading && variant.isLoading),
        ...css,
      }}
    >
      {isLoading ? (
        <svg
          aria-label={formatMessage({ id: 'loading' })}
          width={size === 'xsmall' ? 16 : 20}
          height={size === 'xsmall' ? 16 : 20}
          viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
          role='progressbar'
          css={{
            animation: `${rotate} 1.4s linear infinite`,
          }}
        >
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill='none'
            stroke={spinnerColorOfType(type, theme)}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap='round'
            css={{
              animation: `${dash} 1.4s ease-in-out infinite`,
            }}
          />
        </svg>
      ) : (
        <>
          {Boolean(startIcon) &&
            size !== 'xsmall' &&
            cloneElement(startIcon as ReactElement<IconProps>, {
              size: customIconSize ?? getIconSize(size),
            })}

          {typeof children === 'string' ? (
            <BodyS tag='span' fontWeight='medium'>
              {children}
            </BodyS>
          ) : (
            children
          )}

          {Boolean(endIcon) &&
            size !== 'xsmall' &&
            cloneElement(endIcon as ReactElement<IconProps>, {
              size: customIconSize ?? getIconSize(size),
            })}
        </>
      )}
    </button>
  );
};
